import React, {useCallback, useRef} from 'react';

import {toast} from 'react-toastify';

import {validateCodeWithHsn} from 'api';
import {
    $invoiceLinesHolder,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';
import {$currentScannedGtin, setCurrentScannedGtin} from 'models/documents/invoices/details';
import {InvoiceLine} from 'types/invoices';
import {CodeResponseType} from 'types/validation';
import {invoiceValidationWithTransportCodes} from 'utils/markingCodes';

interface UseCodeScannerProps {
    combineByGtin: boolean;
    handleEnterFromLine: (response: CodeResponseType['info']) => void;
    clearCodeAndDescription: () => void;
    setScanPending: (pending: boolean) => void;
}

interface CodeScannerReturn {
    onEnterCode: (code: string, fromLine?: boolean) => Promise<void>;
    codeGtinHolder: React.MutableRefObject<string>;
}

const DEFAULT_INVOICE_LINE_BASE = {
    qty: '1',
    isMarkedItem: true,
    price: '',
    lines: '',
    vatCategory: {label: '', value: ''},
    vat: '',
    lineAmountWVat: '',
    isHsn: 'HS' as const,
    copyActions: {
        allowToCopy: false,
        allowToDuplicateDown: false,
        allowToPaste: true,
    },
};

function createInvoiceLineFromCode(
    codeInfo: CodeResponseType['info'],
    includeCode = true,
): InvoiceLine {
    return {
        ...DEFAULT_INVOICE_LINE_BASE,
        description: codeInfo.productName,
        serviceCategory: {
            label: codeInfo.hsnDescription ?? codeInfo.hsnCode,
            value: codeInfo.hsnCode,
        },
        gtin: codeInfo.gtin,
        code: includeCode ? codeInfo.identityCode : '',
    };
}

function shouldSkipValidation(code: string, currentCode: string): 'duplicate' | 'short' | null {
    if (currentCode === code) {
        return 'duplicate';
    }
    if (code.length < 10) {
        return 'short';
    }
    return null;
}

function showValidationErrors(errors: string[]): void {
    toast.error(
        <div>
            <strong>Errors</strong>
            <ul style={{margin: '6px 0'}}>
                {errors.map((error) => (
                    <li key={error}>{error}</li>
                ))}
            </ul>
        </div>,
    );
}

export function useCodeScanner({
    combineByGtin,
    handleEnterFromLine,
    clearCodeAndDescription,
    setScanPending,
}: UseCodeScannerProps): CodeScannerReturn {
    const codeGtinHolder = useRef<string>('');

    const handleCombineByGtinMode = useCallback(
        (codeInfo: CodeResponseType['info']) => {
            const currentGtin = $currentScannedGtin.getState();

            if (currentGtin && codeInfo.gtin !== currentGtin) {
                toast.info(
                    'This code cannot be grouped with the current group because it has a different GTIN. It will be added as a separate line.',
                );
            }

            if (currentGtin === null || codeInfo.gtin !== currentGtin) {
                setCurrentScannedGtin(codeInfo.gtin);

                const newLine = {
                    ...createInvoiceLineFromCode(codeInfo, false),
                    combinedCodes: [codeInfo.identityCode],
                };

                setInvoiceLinesHolder([newLine]);
            } else {
                const currentLine = $invoiceLinesHolder.getState()[0];
                const newQty = Number(currentLine.qty) + 1;
                const updatedCodes = [...(currentLine.combinedCodes ?? []), codeInfo.identityCode];

                updateInvoiceLinesHolder({
                    index: 0,
                    data: [
                        {columnId: 'qty', value: newQty.toString()},
                        {columnId: 'combinedCodes', value: updatedCodes},
                    ],
                });
            }
        },
        [combineByGtin],
    );

    const handleNormalMode = useCallback((codeInfo: CodeResponseType['info']) => {
        const newLine = createInvoiceLineFromCode(codeInfo, true);
        setInvoiceLinesHolder([newLine]);
    }, []);

    const onEnterCode = useCallback(
        async (code: string, fromLine = false) => {
            if (fromLine) {
                const skipReason = shouldSkipValidation(code, codeGtinHolder.current);

                if (skipReason === 'duplicate') {
                    return;
                }

                if (skipReason === 'short') {
                    clearCodeAndDescription();
                    codeGtinHolder.current = code;
                    return;
                }
            }

            try {
                setScanPending(true);

                const response = (await validateCodeWithHsn(code)).data;
                const validationErrors = invoiceValidationWithTransportCodes(
                    response,
                    $invoiceLinesHolder.getState(),
                );

                if (validationErrors.length > 0) {
                    showValidationErrors(validationErrors);

                    if (fromLine) {
                        clearCodeAndDescription();
                        codeGtinHolder.current = code;
                    }
                    return;
                }

                if (fromLine) {
                    handleEnterFromLine(response.info);
                    codeGtinHolder.current = code;
                    return;
                }

                if (combineByGtin) {
                    handleCombineByGtinMode(response.info);
                } else {
                    handleNormalMode(response.info);
                }

                setInvoiceLineChargeData(null);
            } catch (error) {
                console.error(error);
                toast.error((error as Error).message ?? 'Invalid code');

                if (fromLine) {
                    clearCodeAndDescription();
                }
                codeGtinHolder.current = code;
            } finally {
                setScanPending(false);
            }
        },
        [
            combineByGtin,
            handleEnterFromLine,
            clearCodeAndDescription,
            setScanPending,
            handleCombineByGtinMode,
            handleNormalMode,
        ],
    );

    return {
        onEnterCode,
        codeGtinHolder,
    };
}
